import {
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
} from "react";
import {
  ICreateEditGaiaobjectFormProps,
  UpdateGaiaObjectRequest,
  GaiaObjectFormRef,
} from "./definitions";
import styles from "./style.module.scss";
import { get } from "react-hook-form";
import { useAppSelector } from "app/store";
import { useTeamNameSelector } from "pages/team_settings/hooks";
import { useNotifications } from "features/notifications";
import { useNavigate } from "react-router-dom";
import {
  Button,
  ColumnLayout,
  Container,
  FormField,
  Header,
  SpaceBetween,
  Spinner,
  Form,
  Box,
  Link,
} from "@amzn/awsui-components-react";
import GaiaObjectName from "components/gaiaobject-name";
import { CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME } from "models/generateCustomAttributesSchema";
import {
  buildGaiaObjectProps,
  extractUniqueGroups,
  AttributeGroupRenderer,
} from "./util";
import { z } from "zod";
import { generateDynamicAttributesDataMap } from "utils/dynamicAttributesUtils";
import { cap } from "utils/date-format";
import { validateGovernmentEntity } from "utils/dropdown-utils";
import { getGaiaobjectName } from "components/gaiaobject-name/GaiaobjectName";
import {
  ControlledInput,
  ControlledSelect,
  ControlledMultiselect,
} from "@amzn/react-hook-form-polaris";
import { getFilteredOptions } from "features/ui-content/utils";
import InputFilesModal from "components/input-files-modal";
import { FileMetadata } from "components/input-files/definitions";
import { GaiaConstants } from "utils";
import { useTeamSpecificGaiaObjectTemplates } from "components/gaiaobjects-table/table-config";
import PermissionBoundary from "components/permission-boundary";
import { createFormAdapter } from "./adapters";
import { FormSection } from "./adapters/definitions";
import { useGaiaObjectUiContent } from "features/ui-content";
import { useCheckTeamSwitch } from "hooks/useCheckTeamSwitch";
import { useGuestsLoader } from "hooks/useGuestsLoader";
import {
  useCreateGaiaObject,
  useUpdateGaiaObject,
  useDocumentUpload,
  useDraftOperations,
  useLoadData,
  useFormInit,
  useFormValidation,
  useSuffixFieldEffects,
  useWizardIntegration,
  useTransformedGaiaObject,
} from "./hooks";

const CreateEditGaiaobjectForm = forwardRef<
  GaiaObjectFormRef,
  ICreateEditGaiaobjectFormProps
>(
  (
    {
      type,
      mode,
      shortId,
      onWizardValidationSuccess,
      onWizardValidationError,
      triggerWizardValidation = 0,
    },
    ref
  ) => {
    const { addNotification } = useNotifications();
    const { teamName } = useTeamNameSelector();
    const navigate = useNavigate();
    const currentUser = useAppSelector((state) => state.auth.user);

    // Use extracted mutation hooks
    const { createGaiaObject } = useCreateGaiaObject();
    const { updateGaiaObject: updateGaiaObjectAPI } = useUpdateGaiaObject();
    const { uploadDocument } = useDocumentUpload();
    const { processDraftOperations } = useDraftOperations();

    const [disableCreateEdit, setDisableCreateEdit] = useState(false);
    const [fileMetadata, setFileMetadata] = useState<FileMetadata[]>([]);
    const [uploadModalVisible, setUploadModalVisible] = useState(false);

    // Create adapter and determine what data to load
    const adapter = useMemo(() => createFormAdapter(mode), [mode]);
    const adapterConfig = adapter.getFormConfig();

    // Use data loading hook to load all the related data
    const {
      gaiaObjectData,
      scanResultData,
      scanJobData,
      contactsList,
      error,
      isLoading,
      isError,
      gaiaObjectLoading,
    } = useLoadData(teamName, adapter, shortId);

    // Load UI content
    const uiContent = useGaiaObjectUiContent(type);

    const defaultCountry: string =
      uiContent?.defaultCountry ?? uiContent?.countryOptions[0]?.value ?? "";

    const isUIContentLoaded = useMemo(() => {
      return !!uiContent?.countryOptions?.length;
    }, [uiContent?.countryOptions]);

    useCheckTeamSwitch(gaiaObjectData?.gaiaObject?.teamName || "");
    useGuestsLoader(adapter.getGuestLoaderId(shortId));

    // Transforms Gaia object data for form usage
    const {
      customAttributes,
      createSchema,
      formSchema,
      transformedGaiaObject,
    } = useTransformedGaiaObject(type, gaiaObjectData, gaiaObjectLoading);

    const defaultStatus: string = useMemo(() => {
      const statusAttribute = customAttributes?.find(
        (attr: any) => attr.attributeName === "status"
      );
      return statusAttribute?.defaultValue || "Not started";
    }, [customAttributes]);

    const formData = {
      uiContent,
      defaultCountry,
      scanJobData,
      scanResultData,
      customAttributes,
      transformedGaiaObject,
      currentUser,
    };

    const { handleSubmit, control, formState, watch, reset, setValue } =
      useFormValidation(formSchema, defaultCountry, defaultStatus, adapter);

    // Initialize form data using adapter pattern to handle different modes (create/edit/draft/wizard)
    const { localFormData, setLocalFormData } = useFormInit(
      shortId,
      reset,
      setValue,
      formData,
      adapter,
      mode
    );

    const getTemplatesByCategory = useTeamSpecificGaiaObjectTemplates(type);

    const country = watch(`${CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME}.country`);

    const filteredGovEntityOptions = useMemo(() => {
      return getFilteredOptions(
        uiContent?.governmentEntityOptions || [],
        uiContent?.countryOptions || [],
        "country",
        country
      );
    }, [uiContent?.governmentEntityOptions, country]);

    const governmentEntity = watch(
      `${CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME}.governmentEntity`
    );

    const localJurisdiction = watch(
      `${CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME}.localJurisdiction`
    );
    const entity = watch(`${CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME}.entity`);
    const noticeType = watch(
      `${CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME}.noticeType`
    );
    const litigationType = watch(
      `${CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME}.litigationType`
    );
    const taxPeriodStartDate = watch(
      `${CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME}.taxPeriodStart`
    );
    const taxPeriodEndDate = watch(
      `${CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME}.taxPeriodEnd`
    );
    const template = watch("template");
    const taxTypes = watch(`${CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME}.taxType`);

    const { titleSuffix } = useSuffixFieldEffects(uiContent, watch, setValue);

    const countryRef = useRef<string>();
    // Reset governmentEntity when country changes
    useEffect(() => {
      if (country && countryRef.current && countryRef.current !== country) {
        setValue(`${CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME}.governmentEntity`, "");
      }
      countryRef.current = country;
    }, [country, setValue]);

    const buildWatchedFieldsData = () => ({
      country,
      governmentEntity,
      localJurisdiction,
      entity,
      noticeType,
      taxPeriodStartDate,
      taxPeriodEndDate,
      litigationType,
      taxTypes,
    });

    const buildGaiaObjectPropsFromFields = () => {
      const watchedFields = buildWatchedFieldsData();
      return buildGaiaObjectProps(
        watchedFields,
        type,
        titleSuffix,
        watch(`${CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME}.entityCode`)
      );
    };

    const onValid = async (data: any) => {
      const currentCountry = data[CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME]?.country;
      const currentGovEntity =
        data[CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME]?.governmentEntity;

      if (
        !validateGovernmentEntity({
          country: currentCountry,
          govEntity: currentGovEntity,
          filteredOptions: filteredGovEntityOptions,
          setValue,
          addNotification,
        })
      ) {
        return;
      }

      // Build submission context
      const context = {
        metadata: {
          type,
          shortId,
          teamName,
        },
        formData: {
          fileMetadata,
          template,
          customAttributes,
          transformedGaiaObject,
          scanJobData,
        },
        ui: {
          setDisableCreateEdit,
          onWizardValidationSuccess,
          onWizardValidationError,
        },
        transforms: {
          buildGaiaObjectPropsFromFields,
          processGaiaObjectData,
          buildUpdateRequest: updateGaiaObjectRequest,
        },
        api: {
          createGaiaObject,
          updateGaiaObject: updateGaiaObjectAPI,
          uploadDocument,
          processDraftOperations,
        },
        adapter,
      };

      // Call adapter's handleSubmit with data and context, for different mode
      await adapter.handleSubmit(data, context);
    };

    const onInvalid = (errors: any) => {
      if (onWizardValidationError) {
        onWizardValidationError(errors);
        return;
      }

      // Optionally, scroll to first error field or show a notification
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const errorElement = document.querySelector(
          `[name="${firstErrorField}"]`
        );
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    };

    // Use wizard integration hook with proper handlers
    const { getWizardImperativeHandleMethods } = useWizardIntegration(
      adapter,
      shortId,
      triggerWizardValidation,
      handleSubmit,
      onValid,
      onInvalid,
      watch
    );

    useImperativeHandle(ref, () =>
      getWizardImperativeHandleMethods(localFormData, setLocalFormData)
    );

    const processGaiaObjectData = (object: z.infer<typeof createSchema>) => {
      const gaiaobjectProps = buildGaiaObjectPropsFromFields();
      object.title = getGaiaobjectName(gaiaobjectProps);
      object.teamName = teamName;
      object.category = type;
      object.templateId = template;

      if (customAttributes) {
        object.customAttributesDataMap = generateDynamicAttributesDataMap(
          customAttributes,
          object[CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME]
        );

        if (mode !== "edit" && !object.customAttributesDataMap?.status) {
          object.customAttributesDataMap = {
            ...object.customAttributesDataMap,
            status: defaultStatus,
          };
        }
      }
      return object;
    };

    const updateGaiaObject = (object: z.infer<typeof createSchema>) => {
      const updatedObject = { ...transformedGaiaObject };
      Object.assign(updatedObject, object);

      const gaiaobjectProps = buildGaiaObjectPropsFromFields();

      updatedObject.gaiaObjectId = transformedGaiaObject.gaiaObjectId;
      updatedObject.title = getGaiaobjectName(gaiaobjectProps);
      updatedObject.teamName = teamName;
      updatedObject.category = type;
      updatedObject.shortId = shortId;

      if (customAttributes) {
        updatedObject.customAttributesDataMap =
          generateDynamicAttributesDataMap(
            customAttributes,
            updatedObject[CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME]
          );
      }

      updatedObject[CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME] = {
        ...updatedObject[CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME],
        effort: transformedGaiaObject?.customAttributesDataMap?.effort,
        status: transformedGaiaObject?.customAttributesDataMap?.status,
        statusActionOwner:
          transformedGaiaObject?.customAttributesDataMap?.statusActionOwner,
        statusDetail:
          transformedGaiaObject?.customAttributesDataMap?.statusDetail,
      };

      return updatedObject;
    };

    const updateGaiaObjectRequest = (object: z.infer<typeof createSchema>) => {
      const updatedGaiaObject = updateGaiaObject(object);

      const request: UpdateGaiaObjectRequest = {
        gaiaObjectId: updatedGaiaObject.gaiaObjectId,
        gaiaObject: updatedGaiaObject,
      };

      return request;
    };

    const handleMultipleFileUpload = (
      metadata: FileMetadata[],
      onComplete: () => void
    ) => {
      setFileMetadata(metadata);
      onComplete();
    };

    const onCancelCreate = () => {
      navigate(adapter.getCancelRedirectPath(type, shortId));
    };

    return (
      <PermissionBoundary
        allowMemberOnly={false}
        shouldCheckPermission={true}
        isLoading={isLoading}
        isError={isError}
        error={error}
      >
        {mode !== "create" && !isUIContentLoaded ? (
          <Box textAlign="center">
            <Spinner /> {`Loading ${type} information...`}
          </Box>
        ) : (
          <form onSubmit={handleSubmit(onValid, onInvalid)}>
            <Form
              actions={
                adapterConfig.enableNavigation && !(mode === "wizard") ? (
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button
                      formAction="none"
                      onClick={onCancelCreate}
                      disabled={disableCreateEdit}
                      variant="link"
                    >
                      Cancel
                    </Button>
                    <Button variant="primary" disabled={disableCreateEdit}>
                      {disableCreateEdit ? <Spinner /> : null}
                      {adapter.getSubmitButtonText()}
                    </Button>
                  </SpaceBetween>
                ) : undefined
              }
            >
              <SpaceBetween size="l">
                <Container
                  header={<Header variant="h2">{cap(type)} information</Header>}
                >
                  <br />
                  <ColumnLayout columns={2}>
                    <GaiaObjectName
                      country={country}
                      governmentEntity={governmentEntity}
                      localJurisdiction={localJurisdiction}
                      entity={entity}
                      noticeType={noticeType}
                      taxPeriodStartDate={taxPeriodStartDate}
                      taxPeriodEndDate={taxPeriodEndDate}
                      litigationType={litigationType}
                      category={type}
                      titleSuffix={titleSuffix}
                      entityCode={watch(
                        `${CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME}.entityCode`
                      )}
                      taxTypes={taxTypes}
                    />
                    <FormField
                      label={
                        <>
                          Name suffix - <i>optional</i>
                        </>
                      }
                      description="Additional information appended to name"
                      errorText={get(formState?.errors, `titleSuffix.message`)}
                    >
                      <ControlledInput name="titleSuffix" control={control} />
                    </FormField>
                  </ColumnLayout>
                  {customAttributes && (
                    <SpaceBetween size="l">
                      <div></div>
                      {extractUniqueGroups(customAttributes).map(
                        (groupName) => {
                          const groupAttributes = customAttributes.filter(
                            (attr: any) => {
                              const isInGroup =
                                (attr?.group || "") === groupName;
                              const isStatusField =
                                attr.attributeName === "status";
                              const shouldHideStatus =
                                mode === "edit" && isStatusField;
                              return isInGroup && !shouldHideStatus;
                            }
                          );

                          return (
                            <AttributeGroupRenderer
                              key={groupName}
                              groupName={groupName}
                              groupAttributes={groupAttributes}
                              formState={formState}
                              control={control}
                              type={type}
                              mode={mode}
                              setValue={setValue}
                              filteredGovEntityOptions={
                                filteredGovEntityOptions
                              }
                            />
                          );
                        }
                      )}
                    </SpaceBetween>
                  )}
                </Container>
                {adapter.shouldShowSection(FormSection.CONTACTS) && (
                  <Container header={<Header variant="h2">Contact</Header>}>
                    <FormField
                      label="Select contact"
                      description="You can select up to 10 contacts"
                      errorText={get(formState?.errors, `contactIds.message`)}
                    >
                      <ControlledMultiselect
                        name={"contactIds"}
                        control={control}
                        placeholder="Search by name or email"
                        filteringType="auto"
                        options={
                          contactsList?.contacts?.contacts.map(
                            (contact: any) => ({
                              value: contact.contactId,
                              label: contact.fullName,
                              description: contact.email,
                            })
                          ) ?? []
                        }
                      />
                    </FormField>
                  </Container>
                )}
                {adapter.shouldShowSection(FormSection.TEMPLATES) && (
                  <Container header={<Header variant="h2">Templates</Header>}>
                    <FormField
                      label={
                        <>
                          Task groups and tasks will be created according to the
                          template. See{" "}
                          <Link href={`/team/?tabId=templates`}>
                            Team Settings
                          </Link>{" "}
                          for detailed information.
                        </>
                      }
                      errorText={get(formState?.errors, `template.message`)}
                    >
                      <ControlledSelect
                        name={"template"}
                        control={control}
                        filteringType="auto"
                        placeholder="Choose a template"
                        options={
                          !getTemplatesByCategory.has(teamName)
                            ? [
                                {
                                  value: undefined,
                                  label:
                                    "There are currently no templates defined for this team",
                                },
                              ]
                            : getTemplatesByCategory.get(teamName)
                        }
                      />
                    </FormField>
                  </Container>
                )}

                {adapter.shouldShowSection(FormSection.FILE_UPLOAD) &&
                  (type === "notice" || type === "litigation") && (
                    <Container
                      header={
                        <Header variant="h2">
                          Upload files -
                          <Box
                            variant="awsui-value-large"
                            fontWeight="bold"
                            fontSize="heading-l"
                          >
                            <i> optional</i>
                          </Box>
                        </Header>
                      }
                    >
                      <br />
                      <FormField>
                        <SpaceBetween direction="vertical" size="s">
                          <Button
                            formAction="none"
                            onClick={() => setUploadModalVisible(true)}
                            iconName="upload"
                          >
                            Choose file
                          </Button>
                          {fileMetadata.length > 0 && (
                            <SpaceBetween direction="vertical" size="xs">
                              {fileMetadata.map((metadata, index) => (
                                <Box key={index} color="text-body-secondary">
                                  {metadata.file.name} - {metadata.documentType}
                                </Box>
                              ))}
                            </SpaceBetween>
                          )}
                        </SpaceBetween>
                      </FormField>
                    </Container>
                  )}
              </SpaceBetween>
            </Form>
          </form>
        )}
        <InputFilesModal
          visible={uploadModalVisible}
          setVisible={setUploadModalVisible}
          onUpload={handleMultipleFileUpload}
          enableDocumentTypes={true}
          requireDocumentTypes={true}
          gaiaObjectType={type}
          dragAndDropEnabled={true}
          description={GaiaConstants.DOCUMENT_STORE_DISCLAIMER}
        />
      </PermissionBoundary>
    );
  }
);

export default CreateEditGaiaobjectForm;
